import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { companyApi } from '../api/endpoints.ts';
import { useSession } from '../state/session.tsx';
import type { ProviderUsageMeter } from '@shared/types/index.ts';

export default function ProviderUsage({ kind }: { kind: 'ai' | 'sms' }) {
  const { t, i18n } = useTranslation(),
    { company, can } = useSession();
  const query = useQuery({
    queryKey: ['provider-usage', company?.id],
    queryFn: () => companyApi.providerUsage(),
    enabled: !!company && can('company:read'),
    staleTime: 30_000,
  });
  if (query.isError)
    return (
      <p className="mb-4 text-sm" role="status">
        {t('providerUsage.unavailable')}
      </p>
    );
  if (!query.data?.active) return null;
  const data = query.data,
    meter = kind === 'ai' ? data.ai : data.sms;
  const money = (micros: number) =>
    new Intl.NumberFormat(i18n.language, { style: 'currency', currency: 'USD' }).format(micros / 1_000_000);
  function row(label: string, value: ProviderUsageMeter) {
    const exhausted = value.committedUsdMicros >= value.allowanceUsdMicros;
    return (
      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
        <span>{label}</span>
        <span className={exhausted ? 'font-semibold text-[color:var(--color-danger)]' : 'tabular-nums'}>
          {money(value.committedUsdMicros)} / {money(value.allowanceUsdMicros)}
        </span>
      </div>
    );
  }
  if (kind === 'sms' && data.smsPayer !== 'platform')
    return (
      <p className="mb-4 text-sm">
        {t(data.smsPayer === 'company' ? 'providerUsage.externalPayer' : 'providerUsage.smsUnconfigured')}
      </p>
    );
  const high =
    meter.committedUsdMicros >= meter.allowanceUsdMicros * 0.8 ||
    (meter.segmentAllowance !== undefined && (meter.segmentsUsed ?? 0) >= meter.segmentAllowance * 0.8);
  return (
    <div
      className="mb-5 rounded border border-[color:var(--color-line)] p-3 text-sm"
      aria-label={t('providerUsage.heading')}
    >
      <p className="mb-2 font-semibold">{t('providerUsage.heading')}</p>
      {kind === 'sms' ? (
        <p className="mb-2">
          {t('providerUsage.segments', { used: meter.segmentsUsed, allowance: meter.segmentAllowance })}
        </p>
      ) : null}
      {row(t(kind === 'ai' ? 'providerUsage.ai' : 'providerUsage.sms'), meter)}
      {kind === 'ai' ? row(t('providerUsage.help'), data.help) : row(t('providerUsage.required'), data.requiredNotices)}
      <p className="mt-2 text-xs text-[color:var(--color-muted)]">{t('providerUsage.rules')}</p>
      {meter.uncertainUsdMicros > 0 ? (
        <p className="mt-2 text-xs">{t('providerUsage.uncertain', { amount: money(meter.uncertainUsdMicros) })}</p>
      ) : null}
      {data.resetAt ? (
        <p className="mt-2 text-xs">
          {t('providerUsage.reset', {
            date: new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium', timeZone: 'UTC' }).format(
              new Date(data.resetAt),
            ),
          })}
        </p>
      ) : null}
      {high ? (
        <p className="mt-2 font-medium text-[color:var(--color-danger)]" role="status">
          {t('providerUsage.warning')}
        </p>
      ) : null}
    </div>
  );
}
